import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, FileSignature, Loader2, ShieldCheck, CreditCard } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function money(n, cur) {
  const sym = cur === 'INR' ? '₹' : cur === 'GBP' ? '£' : cur === 'EUR' ? '€' : '$';
  return `${sym}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export default function QuoteSignPage() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signerName, setSignerName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { invoice_number }
  const [paying, setPaying] = useState(false);
  const [paidConfirmed, setPaidConfirmed] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/api/quotes/sign/${token}`);
      setQuote(r.data);
      if (r.data?.already_signed) setDone({ already: true });
    } catch (e) {
      setError(e?.response?.data?.detail || 'This signing link is invalid or has expired.');
    } finally { setLoading(false); }
  }, [token]);

  // Verify a returned Stripe payment (success_url carries ?paid=1&session_id=...)
  const verifyPayment = useCallback(async (sessionId) => {
    try {
      const r = await axios.get(`${API_BASE}/api/quotes/sign/${token}/payment-status?session_id=${encodeURIComponent(sessionId)}`);
      if (r.data?.paid) setPaidConfirmed(true);
    } catch { /* non-critical */ }
  }, [token]);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get('paid') === '1' && params.get('session_id')) {
      verifyPayment(params.get('session_id'));
    }
  }, [load, verifyPayment]);

  const payNow = async () => {
    setPaying(true); setError('');
    try {
      const r = await axios.post(`${API_BASE}/api/quotes/sign/${token}/checkout`);
      if (r.data?.checkout_url) { window.location.href = r.data.checkout_url; return; }
      setError('Could not start payment.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not start payment.');
    } finally { setPaying(false); }
  };

  // ── Signature canvas (drawn) ──
  const pos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawingRef.current = true; const c = canvasRef.current.getContext('2d'); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawingRef.current) return; e.preventDefault(); const c = canvasRef.current.getContext('2d'); const p = pos(e); c.lineTo(p.x, p.y); c.strokeStyle = '#0f172a'; c.lineWidth = 2; c.lineCap = 'round'; c.stroke(); hasDrawnRef.current = true; };
  const end = () => { drawingRef.current = false; };
  const clearCanvas = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); hasDrawnRef.current = false; };

  const submit = async () => {
    if (!signerName.trim()) { setError('Please type your full name.'); return; }
    if (!agreed) { setError('Please agree to the terms to sign.'); return; }
    setSubmitting(true); setError('');
    let signature = signerName.trim();
    let signature_type = 'typed';
    if (hasDrawnRef.current && canvasRef.current) {
      signature = canvasRef.current.toDataURL('image/png');
      signature_type = 'drawn';
    }
    try {
      const r = await axios.post(`${API_BASE}/api/quotes/sign/${token}/accept`, {
        signer_name: signerName.trim(), signature, signature_type, agreed: true,
      });
      setDone({ invoice_number: r.data?.invoice_number });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not submit your signature. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-7 w-7 animate-spin text-violet-500" /></div>;
  }

  if (error && !quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="quote-sign-error">
        <div className="max-w-md text-center">
          <FileSignature className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-700 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="quote-sign-page">
      <div className="max-w-2xl mx-auto">
        {/* Brand header */}
        <div className="flex items-center gap-3 mb-6">
          {quote.company_logo
            ? <img src={quote.company_logo} alt="" className="h-9 w-9 rounded-lg object-contain bg-white border" />
            : <div className="h-9 w-9 rounded-lg bg-violet-600 flex items-center justify-center text-white font-bold">{(quote.company_name || 'L')[0]}</div>}
          <div>
            <p className="font-semibold text-slate-900">{quote.company_name}</p>
            <p className="text-xs text-slate-500">Quote {quote.quote_number}</p>
          </div>
        </div>

        {done ? (
          <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-8 text-center" data-testid="quote-sign-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
            <h1 className="text-xl font-bold text-slate-900">
              {done.already ? 'This quote was already signed' : 'Quote accepted — thank you!'}
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              {done.invoice_number
                ? `An invoice (${done.invoice_number}) has been generated. The team will be in touch shortly.`
                : `${quote.company_name} has been notified and will follow up with next steps.`}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h1 className="text-lg font-bold text-slate-900">Review &amp; Sign Your Quote</h1>
              <p className="text-sm text-slate-500">Prepared for {quote.client_name || 'you'}</p>
            </div>

            {/* Line items */}
            <div className="p-6 space-y-2">
              {(quote.line_items || []).map((it, i) => (
                <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-50" data-testid={`sign-line-${i}`}>
                  <span className="text-slate-700">{it.description || 'Item'} <span className="text-slate-400">× {it.quantity || 1}</span></span>
                  <span className="text-slate-900 font-medium">{money(it.total || (it.quantity * it.price), quote.currency)}</span>
                </div>
              ))}
              <div className="pt-3 space-y-1 text-sm">
                <Row label="Subtotal" val={money(quote.subtotal, quote.currency)} />
                {quote.discount_amount > 0 && <Row label="Discount" val={`- ${money(quote.discount_amount, quote.currency)}`} />}
                {quote.tax_amount > 0 && <Row label="Tax" val={money(quote.tax_amount, quote.currency)} />}
                <div className="flex justify-between pt-2 mt-1 border-t border-slate-200">
                  <span className="font-semibold text-slate-900">Total</span>
                  <span className="font-bold text-lg text-slate-900" data-testid="sign-total">{money(quote.total_amount, quote.currency)}</span>
                </div>
              </div>
              {quote.terms && <p className="text-xs text-slate-500 mt-4 whitespace-pre-wrap"><span className="font-medium text-slate-600">Terms: </span>{quote.terms}</p>}
            </div>

            {/* Signature */}
            <div className="p-6 bg-slate-50/60 border-t border-slate-100 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-600">Full name</label>
                <input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Type your full name"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  data-testid="sign-name-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-slate-600">Signature (optional — draw below)</label>
                  <button type="button" onClick={clearCanvas} className="text-xs text-slate-400 hover:text-slate-600">Clear</button>
                </div>
                <canvas
                  ref={canvasRef} width={520} height={120}
                  className="w-full h-[120px] bg-white rounded-lg border border-slate-300 touch-none cursor-crosshair"
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                  data-testid="sign-canvas"
                />
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" data-testid="sign-agree-checkbox" />
                <span>I agree to the terms of this quote and authorize it electronically. I understand this signature is legally binding.</span>
              </label>
              {error && <p className="text-sm text-rose-600" data-testid="sign-error-msg">{error}</p>}
              <button
                onClick={submit} disabled={submitting}
                className="w-full bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2 transition-colors"
                data-testid="sign-submit-button"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
                Accept &amp; Sign — {money(quote.total_amount, quote.currency)}
              </button>

              {/* Optional online payment (Stripe Connect) */}
              {paidConfirmed ? (
                <div className="flex items-center justify-center gap-2 text-sm text-emerald-600 font-medium" data-testid="payment-confirmed">
                  <CheckCircle2 className="h-4 w-4" /> Payment received — thank you!
                </div>
              ) : quote.payments_enabled && quote.amount_due > 0 ? (
                <button
                  onClick={payNow} disabled={paying}
                  className="w-full border border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-60 font-semibold rounded-lg py-3 flex items-center justify-center gap-2 transition-colors"
                  data-testid="pay-now-button"
                >
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  Pay {quote.payment_kind === 'deposit' ? 'deposit' : 'now'} — {money(quote.amount_due, quote.currency)}
                </button>
              ) : null}

              <p className="text-[11px] text-slate-400 flex items-center justify-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Secure e-signature · timestamped &amp; IP-logged
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, val }) {
  return (
    <div className="flex justify-between text-slate-600">
      <span>{label}</span><span>{val}</span>
    </div>
  );
}
